import { useState, useEffect, useCallback, useRef } from "react";
import { TrendingUp, ChevronRight, DollarSign, Loader2, RefreshCw } from "lucide-react";
import { PostLoadCard, PostLoadCardHeader } from "@/components/post-load/PostLoadCard";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { computeSpotRate } from "@/lib/spotRateEngine";
import RepricingReviewDialog, { type RepricingLoad } from "./RepricingReviewDialog";

const MAX_LOADS = 50;
const BATCH_SIZE = 5;
const TIMEOUT_MS = 15_000;

export default function AILoadRepricing() {
  const { user } = useAuth();
  const [underpricedLoads, setUnderpricedLoads] = useState<RepricingLoad[]>([]);
  const [estimatedUplift, setEstimatedUplift] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const abortedRef = useRef(false);
  const hasRunRef = useRef(false);

  const analyze = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    hasRunRef.current = true;
    setLoading(true);
    setProgress({ done: 0, total: 0 });
    abortedRef.current = false;

    // Safety timeout — show partial results after 15s
    const partialResults: RepricingLoad[] = [];
    const timeoutId = setTimeout(() => {
      abortedRef.current = true;
      partialResults.sort((a, b) => b.delta - a.delta);
      setUnderpricedLoads([...partialResults]);
      setEstimatedUplift(partialResults.reduce((s, l) => s + l.delta, 0));
      setLoading(false);
    }, TIMEOUT_MS);

    try {
      // First, get ALL user load IDs for exclusion from comp pool
      const { data: allUserLoads } = await supabase
        .from("loads")
        .select("id")
        .eq("created_by", user.id)
        .eq("status", "posted");
      const excludeLoadIds = (allUserLoads || []).map((l: any) => l.id);

      const { data: rawData } = await supabase
        .from("loads")
        .select(
          "id, load_number, origin_city, origin_state, origin_latitude, origin_longitude, " +
          "destination_city, destination_state, destination_latitude, destination_longitude, " +
          "rate_amount, total_miles, equipment_type"
        )
        .eq("created_by", user.id)
        .eq("status", "posted")
        .not("rate_amount", "is", null)
        .not("total_miles", "is", null)
        .gt("total_miles", 0)
        .gt("rate_amount", 0)
        .not("origin_latitude", "is", null)
        .not("destination_latitude", "is", null)
        .order("posted_at", { ascending: false })
        .limit(MAX_LOADS);

      const data = rawData as unknown as Array<{
        id: string; load_number: string;
        origin_city: string; origin_state: string; origin_latitude: number; origin_longitude: number;
        destination_city: string; destination_state: string; destination_latitude: number; destination_longitude: number;
        rate_amount: number; total_miles: number; equipment_type: string;
      }> | null;

      if (!data?.length) {
        clearTimeout(timeoutId);
        setUnderpricedLoads([]);
        setLoading(false);
        return;
      }

      setProgress({ done: 0, total: data.length });

      for (let i = 0; i < data.length; i += BATCH_SIZE) {
        if (abortedRef.current) break;

        const batch = data.slice(i, i + BATCH_SIZE);
        const batchResults = await Promise.allSettled(
          batch.map(async (load) => {
            const spotResult = await computeSpotRate({
              originLat: Number(load.origin_latitude),
              originLon: Number(load.origin_longitude),
              destLat: Number(load.destination_latitude),
              destLon: Number(load.destination_longitude),
              laneMiles: load.total_miles!,
              equipmentType: load.equipment_type,
              excludeLoadIds,
            }, { skipTrend: true });

            if (!spotResult) return null;

            const currentRpm = load.rate_amount! / load.total_miles!;
            if (currentRpm >= spotResult.marketRpm * 0.92) return null;

            return {
              id: load.id,
              loadNumber: load.load_number,
              originCity: load.origin_city,
              originState: load.origin_state,
              destCity: load.destination_city,
              destState: load.destination_state,
              currentRate: Math.round(load.rate_amount!),
              currentRpm: Math.round(currentRpm * 100) / 100,
              suggestedRate: spotResult.suggestedPostRate,
              suggestedRpm: Math.round((spotResult.suggestedPostRate / load.total_miles!) * 100) / 100,
              totalMiles: load.total_miles!,
              delta: spotResult.suggestedPostRate - load.rate_amount!,
              confidence: spotResult.confidence,
              compCount: spotResult.compCount,
            } as RepricingLoad;
          })
        );

        for (const r of batchResults) {
          if (r.status === "fulfilled" && r.value) partialResults.push(r.value);
        }

        if (!abortedRef.current) {
          setProgress({ done: Math.min(i + BATCH_SIZE, data.length), total: data.length });
        }
      }

      if (!abortedRef.current) {
        clearTimeout(timeoutId);
        partialResults.sort((a, b) => b.delta - a.delta);
        setUnderpricedLoads(partialResults);
        setEstimatedUplift(partialResults.reduce((s, l) => s + l.delta, 0));
        setLoading(false);
      }
    } catch (err) {
      console.error("AILoadRepricing error:", err);
      clearTimeout(timeoutId);
      if (!abortedRef.current) setLoading(false);
    }
  }, [user]);

  // Removed auto-run useEffect — analyze only on manual click

  const topAffected = underpricedLoads.slice(0, 3);

  // Demo data shown before scan or when no results
  const showDemo = !hasRunRef.current && !loading;
  const demoLoads = [
    { lane: "Dallas, TX → Atlanta, GA", delta: 340 },
    { lane: "Chicago, IL → Miami, FL", delta: 215 },
    { lane: "Phoenix, AZ → Houston, TX", delta: 180 },
  ];
  const demoUplift = 735;

  return (
    <>
      <PostLoadCard>
        <PostLoadCardHeader
          icon={<TrendingUp className="h-3.5 w-3.5" style={{ color: "hsl(170 50% 15%)" }} />}
          title="ATLAS Load Repricing"
          demoHintKey="section.loadRepricing"
          variant="atlas"
        >
          {!loading && underpricedLoads.length > 0 && (
            <button onClick={() => analyze()} className="text-muted-foreground hover:text-foreground transition-colors">
              <RefreshCw className="h-3 w-3" />
            </button>
          )}
        </PostLoadCardHeader>
        <div className="px-3 pb-3 pt-3 space-y-2.5">
          {loading ? (
            <div className="flex items-center justify-center py-4 text-muted-foreground gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-xs">
                {progress.total > 0
                  ? `Analyzing ${progress.done} of ${progress.total} loads…`
                  : "Analyzing posted rates vs market…"}
              </span>
            </div>
          ) : showDemo ? (
            <>
              <div className="rounded-md px-3 py-2 flex items-center gap-2" style={{ backgroundColor: "hsl(35 95% 50% / 0.10)" }}>
                <DollarSign className="h-3 w-3 shrink-0" style={{ color: "hsl(35 95% 50%)" }} />
                <span className="text-[11px] font-semibold" style={{ color: "hsl(35 80% 42%)" }}>
                  3 loads priced below market
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold" style={{ color: "hsl(var(--brand-teal-deep))" }}>
                    +${demoUplift.toLocaleString()}
                  </span>
                  <span className="text-[10px] text-muted-foreground">est. uplift</span>
                </div>
              </div>

              <div className="space-y-1">
                <span className="text-[9px] text-muted-foreground uppercase tracking-wide font-medium">Top affected lanes</span>
                {demoLoads.map((d) => (
                  <div key={d.lane} className="flex items-center justify-between text-[11px]">
                    <span className="text-foreground font-medium truncate mr-2">{d.lane}</span>
                    <span className="font-semibold shrink-0" style={{ color: "hsl(var(--brand-teal-deep))" }}>
                      +${d.delta.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => analyze()}
                className="flex items-center justify-center gap-2 text-[11px] font-semibold w-full rounded-md px-3 py-3 transition-colors"
                style={{
                  backgroundColor: "hsl(170 85% 32% / 0.12)",
                  color: "hsl(var(--brand-teal-deep))",
                }}
              >
                <TrendingUp className="h-3.5 w-3.5" />
                Scan Loads for Repricing Opportunities
              </button>
            </>
          ) : underpricedLoads.length === 0 ? (
            <p className="text-[11px] text-muted-foreground py-2">All loads are priced at or above market rate.</p>
          ) : (
            <>
              <div className="rounded-md px-3 py-2 flex items-center gap-2" style={{ backgroundColor: "hsl(35 95% 50% / 0.10)" }}>
                <DollarSign className="h-3 w-3 shrink-0" style={{ color: "hsl(35 95% 50%)" }} />
                <span className="text-[11px] font-semibold" style={{ color: "hsl(35 80% 42%)" }}>
                  {underpricedLoads.length} load{underpricedLoads.length !== 1 ? "s" : ""} priced below market
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold" style={{ color: "hsl(var(--brand-teal-deep))" }}>
                    +${Math.round(estimatedUplift).toLocaleString()}
                  </span>
                  <span className="text-[10px] text-muted-foreground">est. uplift</span>
                </div>
              </div>

              {topAffected.length > 0 && (
                <div className="space-y-1">
                  <span className="text-[9px] text-muted-foreground uppercase tracking-wide font-medium">Top affected lanes</span>
                  {topAffected.map((load) => (
                    <div key={load.id} className="flex items-center justify-between text-[11px]">
                      <span className="text-foreground font-medium truncate mr-2">
                        {load.originCity}, {load.originState} → {load.destCity}, {load.destState}
                      </span>
                      <span className="font-semibold shrink-0" style={{ color: "hsl(var(--brand-teal-deep))" }}>
                        +${Math.round(load.delta).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={() => setDialogOpen(true)}
                className="flex items-center gap-1 text-[11px] font-semibold w-full rounded-md px-3 py-2 transition-colors"
                style={{
                  backgroundColor: "hsl(170 85% 32% / 0.12)",
                  color: "hsl(var(--brand-teal-deep))",
                }}
              >
                Review & Reprice {underpricedLoads.length} Load{underpricedLoads.length !== 1 ? "s" : ""}
                <ChevronRight className="h-3 w-3 ml-auto" />
              </button>
            </>
          )}
        </div>
      </PostLoadCard>

      <RepricingReviewDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        loads={underpricedLoads}
        onApplied={analyze}
      />
    </>
  );
}